const getFirstValue = (event) => event.exception?.values?.[0]

/**
 * 401/403/404 while loading a public URL means the viewer can't see the resource or it's gone. That's an
 * expected outcome, not a failure to report, so call sites skip `reportToSentry` for these.
 */
const isExpectedError = (error) => [401, 403, 404].includes(error?.response?.status)

/**
 * Filter out Stream Chat errors.
 */
const streamChatFilters = (event, hint) => {
  const error = hint?.originalException

  // The Stream Chat SDK deleting a draft that no longer exists; not user-impacting
  if (error?.message?.includes('DeleteDraft: draft not found')) {
    return null
  }
  return event
}

/**
 * Filter out network errors.
 */
const networkFilters = (event) => {
  const firstValue = getFirstValue(event)

  if (firstValue?.type === 'AxiosError') {
    const msg = firstValue.value
    if (msg?.includes('Network Error') || msg?.includes('Request aborted')) {
      return null
    }
  }

  if (firstValue?.value?.match(/NetworkError|Load failed|Failed to fetch/)) {
    return null
  }

  return event
}

/**
 * Filter out IndexedDB errors.
 */
const indexDbFilters = (event) => {
  const idbErrors = ['UnknownError', 'AbortError', 'VersionError', 'QuotaExceededError', 'DataError']
  const firstValue = getFirstValue(event)

  if (idbErrors.some((e) => firstValue?.type?.includes(e))) {
    return null
  }

  // IndexedDB DOMExceptions surface as type "Error" with the name prefixed to the message
  if (firstValue?.value?.match(/^(UnknownError|VersionError): /)) {
    return null
  }

  if (
    firstValue?.value?.match(
      /Indexed Database|database file on disk|in-progress transaction|Database deleted by request of the user/i
    )
  ) {
    return null
  }

  return event
}

/**
 * Filter out errors where the URL is for the local hard drive.
 */
const savedToDiskFilters = (event) => {
  const url = event.request?.url || event.tags?.url || ''
  const firstValue = getFirstValue(event)

  if (url.startsWith('file://') || url.includes('/C:/') || (url.includes('/Users/') && url.includes('.html'))) {
    return null
  }

  // Adobe Launch failing on a page saved to disk and opened offline
  if (firstValue?.value?.includes('_satellite._runScript')) {
    return null
  }

  return event
}

/**
 * Stack frame markers for scripts we don't own: the bitmovin player, Dynatrace RUM (`ruxitagent`, `dtAWF`),
 * Adobe Launch, and Facebook's in-app browser logger (`iabjs`).
 */
const thirdPartyFrameMarkers = ['bitmovin-player', 'ruxitagent', 'dtAWF', 'c247cd0acad1/launch', 'iabjs']

/**
 * Filter out errors that are from 3rd party libraries.
 */
const thirdPartyFilters = (event) => {
  const firstValue = getFirstValue(event)

  const errorMsg = firstValue?.value || ''
  const frames = firstValue?.stacktrace?.frames || []
  const isThirdPartyFrame = frames.some((frame) =>
    thirdPartyFrameMarkers.some((marker) => frame?.filename?.includes(marker) || frame?.function?.includes(marker))
  )

  if (
    errorMsg.includes('postEvent') ||
    errorMsg.includes('invalid origin') ||
    errorMsg.includes('runtime.sendMessage') ||
    errorMsg.includes('Unable to create AppMeasurement tracker') ||
    errorMsg.includes('webkit.messageHandlers') || // scripts injected by iOS in-app browsers (WKWebView)
    isThirdPartyFrame
  ) {
    return null
  }

  return event
}

/**
 * Fingerprint AxiosErrors from requests Memories doesn't own into their own issues instead of the shared axios
 * `settle` catch-all. They're regrouped rather than dropped so the resulting issues can be archived "until
 * escalating" in Sentry and still re-alert on a real spike. Keyed on request URL (read from the AxiosError,
 * since it's scrubbed from the event) plus status, so a 5xx outage doesn't hide behind routine 403 noise.
 */
const fingerprintExternalRequests = (event, hint) => {
  const error = hint?.originalException
  if (error?.name !== 'AxiosError') return event

  const url = error.config?.url || error.response?.config?.url || error.request?.responseURL || ''
  const status = error.response?.status

  // Shared frame services (chef/header, oats/outages), often blocked by the WAF for some clients
  if (url.includes('/service/home/utils/')) {
    event.fingerprint = ['external-request', 'service/home/utils', String(status ?? 'unknown')]
  }

  // zion-image-viewer record metadata lookups; a 404 is the expected state for some record-backed memories
  if (url.includes('/service/records/') && status === 404) {
    event.fingerprint = ['external-request', 'service/records', '404']
  }

  return event
}

/**
 * Fingerprint errors reported via `reportToSentry` by operation and status. Those pass the raw AxiosError,
 * whose only frames are axios internals (`settle`), so without this every operation and status collapses into
 * one issue where expected 404s and 401s bury real regressions. Same regroup-don't-drop approach as
 * `fingerprintExternalRequests`.
 *
 * Scoped to events with an `operation` tag so uncaught Memories errors keep their natural grouping.
 *
 * Don't gate this on stack frames. `beforeSend` sees minified chunk URLs (source maps are applied server-side),
 * so a filename check like `node_modules` never matches in production.
 */
const fingerprintMemoriesRequests = (event, hint) => {
  // An external-request fingerprint already claimed this event
  if (event.fingerprint) return event

  const error = hint?.originalException
  if (error?.name !== 'AxiosError') return event

  const operation = event.tags?.operation
  if (!operation) return event

  const status = String(error.response?.status ?? 'unknown')

  // The status otherwise lives only in the exception message, so tag it for search
  event.tags = { ...event.tags, http_status: status }
  event.fingerprint = ['memories', operation, status]

  return event
}

/**
 * Filter out errors caused by DOM manipulation.
 */
const domManipulationFilters = (event) => {
  const errorMsg = event.exception?.values?.[0]?.value || ''
  if (errorMsg.match(/insertBefore|removeChild.*Node|object can not be found here/)) {
    return null
  }

  return event
}

// Normalize dynamic URL segments so errors group by route rather than by unique ID
const normalizeTransaction = (event) => {
  if (event.transaction) {
    event.transaction = event.transaction
      .replace(/\/memory\/print\/[^/]+/, '/memory/print/:id')
      .replace(/\/memory\/(?!print)[^/]+(\/[^/]+)?/, '/memory/:id')
      .replace(/\/gallery\/album\/[^/]+(\/[^/]+)?/, '/gallery/album/:id')
      .replace(/\/tree\/[^/]+/, '/tree/:pid')
  }
  return event
}

const filters = [
  domManipulationFilters,
  indexDbFilters,
  networkFilters,
  savedToDiskFilters,
  streamChatFilters,
  thirdPartyFilters,
]
const beforeSend = (event, hint) => {
  const filtered = filters.reduce((currentEvent, filter) => {
    return currentEvent === null ? null : filter(currentEvent, hint)
  }, event)
  if (filtered === null) return null

  const fingerprinted = fingerprintMemoriesRequests(fingerprintExternalRequests(filtered, hint), hint)
  return normalizeTransaction(fingerprinted)
}

export { beforeSend, isExpectedError }
